import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    MdInventory2, MdShoppingCart, MdWarning, MdArrowForward,
    MdMenuBook, MdDelete, MdReceipt, MdRefresh, MdTrendingUp,
    MdTrendingDown, MdFilterList, MdAttachMoney, MdCategory,
    MdLocalDining, MdKitchen, MdPieChart, MdRestaurantMenu,
} from 'react-icons/md';
import {
    PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
    ResponsiveContainer, LineChart, Line, CartesianGrid, Area, AreaChart,
} from 'recharts';
import { getRestaurantInventory, getRestaurantInventoryStats } from '../../services/restaurantInventoryService';
import { getOrders } from '../../services/orderService';
import { getInvoices } from '../../services/invoiceService';
import { getMenuItems, MENU_CATEGORIES, getCategoryInfo, calcPortionCost } from '../../services/menuService';
import { getEposEvents } from '../../services/eposService';
import EposSalesTable from '../../components/epos/EposSalesTable';
import toast from 'react-hot-toast';
import { seedRestaurantDashboard } from '../../scripts/seedRestaurantDashboard';
import './Dashboard.css';

/* ═══ Chart colour palette ═══ */
const COLORS = [
    '#c9a96e', '#3b82f6', '#22c55e', '#f59e0b',
    '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899',
    '#14b8a6', '#f97316', '#6366f1', '#84cc16',
];
const PIE_COLORS = ['#c9a96e', '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];

/* ═══ Tooltip styling ═══ */
const tooltipStyle = {
    backgroundColor: 'var(--color-bg-elevated)',
    border: '1px solid var(--color-border)',
    borderRadius: 8,
    fontSize: 12,
    boxShadow: '0 4px 12px rgba(0,0,0,.3)',
};

/* ═══ Custom Pie Label — percentage only inside, names in legend ═══ */
const renderPieLabel = ({ percent }) =>
    percent > 0.04 ? `${(percent * 100).toFixed(0)}%` : '';

/* ═══ Date Parser ═══ */
const parseDate = (d) => {
    if (!d) return new Date(0);
    if (d instanceof Date) return d;
    if (typeof d.toDate === 'function') return d.toDate();
    if (d.seconds) return new Date(d.seconds * 1000);
    // Date-only strings like '2026-06-02': interpret as midnight in business TZ (Europe/London)
    if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
        const [y, m, day] = d.split('-').map(Number);
        const guess = new Date(y, m - 1, day, 0, 0, 0, 0);
        const inTz = new Date(guess.toLocaleString('en-US', { timeZone: 'Europe/London' }));
        return new Date(guess.getTime() + (guess - inTz));
    }
    return new Date(d);
};

const RestaurantDashboard = () => {
    const { currentUser, userProfile } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const { id } = useParams();
    const location = useLocation();
    const restaurantId = id || currentUser?.uid;

    /* ─── raw data ─── */
    const [inventoryItems, setInventoryItems] = useState([]);
    const [invStats, setInvStats] = useState(null);
    const [orders, setOrders] = useState([]);
    const [eposEvents, setEposEvents] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [seeding, setSeeding] = useState(false);

    /* ─── filters ─── */
    const [menuCatFilter, setMenuCatFilter] = useState('all');
    const [invTypeFilter, setInvTypeFilter] = useState('all');
    const [dateRange, setDateRange] = useState('week');
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');

    /* ─── UK business timezone for consistent date filtering ─── */
    const BUSINESS_TZ = 'Europe/London';

    /** Get current date parts in the business timezone (Europe/London) */
    const getNowInBusinessTz = useCallback(() => {
        const now = new Date();
        // Format current instant in the business timezone to extract year/month/day
        const parts = new Intl.DateTimeFormat('en-GB', {
            timeZone: BUSINESS_TZ,
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            hour12: false,
        }).formatToParts(now);
        const get = (type) => parseInt(parts.find(p => p.type === type)?.value || '0', 10);
        return {
            year: get('year'),
            month: get('month') - 1, // JS months are 0-based
            day: get('day'),
            hour: get('hour'),
            minute: get('minute'),
            second: get('second'),
            now,
        };
    }, []);

    /** Convert a date + time-of-day in business TZ to an absolute Date object */
    const businessTzDate = useCallback((year, month, day, h = 0, m = 0, s = 0, ms = 0) => {
        // Calculate offset WITHOUT milliseconds (toLocaleString drops ms, causing rounding errors)
        const guess = new Date(year, month, day, h, m, s, 0);
        const localStr = guess.toLocaleString('en-US', { timeZone: BUSINESS_TZ });
        const localDate = new Date(localStr);
        const offset = guess - localDate;
        // Add ms back after offset calculation to avoid double-counting
        return new Date(guess.getTime() + offset + ms);
    }, []);

    /* ─── date range helper (uses UK business timezone) ─── */
    const getDateRangeFilter = useCallback(() => {
        const { year, month, day, now } = getNowInBusinessTz();
        let from;
        switch (dateRange) {
            case 'today': from = businessTzDate(year, month, day); break;
            case 'yesterday': from = businessTzDate(year, month, day - 1); break;
            case '3days': from = new Date(now.getTime() - 3 * 86400000); break;
            case 'week': from = new Date(now.getTime() - 7 * 86400000); break;
            case 'month': from = new Date(now.getTime() - 30 * 86400000); break;
            case 'custom': from = customFrom ? businessTzDate(
                ...customFrom.split('-').map((v, i) => i === 1 ? Number(v) - 1 : Number(v))
            ) : new Date(0); break;
            default: from = new Date(0);
        }
        let to;
        if (dateRange === 'yesterday') {
            to = businessTzDate(year, month, day - 1, 23, 59, 59, 999);
        } else if (dateRange === 'custom' && customTo) {
            const [cy, cm, cd] = customTo.split('-').map(Number);
            to = businessTzDate(cy, cm - 1, cd, 23, 59, 59, 999);
        } else {
            to = now;
        }
        return { from, to };
    }, [dateRange, customFrom, customTo, getNowInBusinessTz, businessTzDate]);

    const filterByDate = useCallback((items, dateField = 'created_at') => {
        const { from, to } = getDateRangeFilter();
        return (items || []).filter(item => {
            const d = parseDate(item[dateField]);
            return d >= from && d <= to;
        });
    }, [getDateRangeFilter]);

    /* ─── filtered EPOS events (uses order_date first — the business date of the sale) ─── */
    const filteredEposEvents = useMemo(() => {
        const { from, to } = getDateRangeFilter();
        return eposEvents
            .filter(e => e.processing_status === 'processed' || e.processing_status === 'has_unmapped')
            .filter(e => {
                // Prefer order_date (business date) over received_at (webhook receipt time)
                const d = e.order_date ? parseDate(e.order_date)
                        : e.received_at ? parseDate(e.received_at)
                        : null;
                return d && d >= from && d <= to;
            });
    }, [eposEvents, getDateRangeFilter]);

    /* ═══════════════════════════════════════ FETCH ═══ */
    const fetchAll = useCallback(async () => {
        if (!restaurantId) return;
        setLoading(true);
        try {
            // Phase 1: Core dashboard data (KPIs, orders, inventory)
            const [stats, inv, ord, inv2] = await Promise.all([
                getRestaurantInventoryStats(restaurantId),
                getRestaurantInventory(restaurantId).catch(() => []),
                getOrders({ restaurant_id: restaurantId }).catch(() => []),
                getInvoices({ restaurant_id: restaurantId }).catch(() => []),
            ]);
            setInvStats(stats);
            setInventoryItems(inv || []);
            setOrders(ord || []);
            setInvoices(inv2 || []);
        } catch (err) {
            console.error('Dashboard load error:', err);
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }

        // Phase 2: EPOS + Menu data (deferred — loads in background after UI renders)
        try {
            const [menu, eposEvts] = await Promise.all([
                getMenuItems(restaurantId).catch(() => []),
                getEposEvents(restaurantId, { limit: 1000 }).catch(() => []),
            ]);
            setMenuItems(menu || []);
            setEposEvents(eposEvts || []);
        } catch (err) {
            console.error('EPOS/Menu load error:', err);
        }
    }, [restaurantId]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    /* ═══════════════════════════════════════ COMPUTED DATA ═══ */

    // ─── ORDER STATS ───
    const orderAnalytics = useMemo(() => {
        const all = filterByDate(orders || []);
        const active = all.filter(o => ['pending', 'confirmed', 'preparing', 'ready'].includes(o.status));
        const delivered = all.filter(o => o.status === 'delivered');
        const cancelled = all.filter(o => o.status === 'cancelled');
        const totalAmount = delivered.reduce((s, o) => s + (o.total || o.total_amount || 0), 0);

        // Orders by status (pie)
        const statusMap = {};
        all.forEach(o => {
            const st = o.status || 'unknown';
            statusMap[st] = (statusMap[st] || 0) + 1;
        });
        const byStatus = Object.entries(statusMap).map(([name, value]) => ({ name, value }));

        // Orders over time (bar/line)
        const { from, to } = getDateRangeFilter();
        const diffDays = Math.ceil((to - from) / (1000 * 60 * 60 * 24));
        const isDayView = diffDays <= 45;

        const byPeriod = {};
        all.forEach(o => {
            const d = parseDate(o.created_at);
            const key = isDayView
                ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
                : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            const label = isDayView
                ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                : d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });

            if (!byPeriod[key]) byPeriod[key] = { period: label, orders: 0, amount: 0 };
            byPeriod[key].orders++;
            byPeriod[key].amount += (o.total || o.total_amount || 0);
        });
        const trend = Object.keys(byPeriod).sort().map(k => ({
            ...byPeriod[k],
            amount: Number(byPeriod[k].amount.toFixed(2)),
        }));

        // Recent 5
        const recent = [...all]
            .sort((a, b) => parseDate(b.created_at) - parseDate(a.created_at))
            .slice(0, 5);

        // Avg order value
        const avgValue = delivered.length ? totalAmount / delivered.length : 0;

        return {
            total: all.length,
            active: active.length,
            delivered: delivered.length,
            cancelled: cancelled.length,
            totalAmount,
            avgValue,
            byStatus,
            trend,
            recent,
        };
    }, [orders, filterByDate, getDateRangeFilter]);

    // ─── INVENTORY ANALYTICS ───
    const inventoryAnalytics = useMemo(() => {
        let items = inventoryItems || [];
        if (invTypeFilter !== 'all') {
            items = items.filter(i => (i.item_type || 'other') === invTypeFilter);
        }
        const lowStock = items.filter(i => (i.current_stock || 0) <= (i.low_stock_threshold || 5));
        const outOfStock = items.filter(i => (i.current_stock || 0) <= 0);
        const totalValue = items.reduce((s, i) => s + ((i.current_stock || 0) * (i.cost_price || 0)), 0);

        // By type (pie)
        const typeMap = {};
        items.forEach(i => {
            const t = i.item_type || 'other';
            if (!typeMap[t]) typeMap[t] = { name: t, count: 0, value: 0 };
            typeMap[t].count++;
            typeMap[t].value += (i.current_stock || 0) * (i.cost_price || 0);
        });
        const byType = Object.values(typeMap);

        // By category (bar)
        const catMap = {};
        items.forEach(i => {
            const c = i.category_name || i.category || 'Uncategorised';
            if (!catMap[c]) catMap[c] = { name: c, count: 0, value: 0 };
            catMap[c].count++;
            catMap[c].value += (i.current_stock || 0) * (i.cost_price || 0);
        });
        const byCategory = Object.values(catMap).sort((a, b) => b.value - a.value).slice(0, 10);

        // Top 10 by stock value
        const topByValue = [...items]
            .map(i => ({
                name: i.item_name || i.name || 'Unknown',
                value: Number(((i.current_stock || 0) * (i.cost_price || 0)).toFixed(2)),
            }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 10);

        // Stock health distribution (pie)
        const healthDist = [
            { name: 'Healthy', value: items.length - lowStock.length },
            { name: 'Low Stock', value: lowStock.length - outOfStock.length },
            { name: 'Out of Stock', value: outOfStock.length },
        ].filter(d => d.value > 0);

        return {
            total: items.length,
            lowStock: lowStock.length,
            outOfStock: outOfStock.length,
            totalValue,
            byType,
            byCategory,
            topByValue,
            healthDist,
            lowStockItems: lowStock.slice(0, 8),
        };
    }, [inventoryItems, invTypeFilter]);

    // ─── MENU ANALYTICS ───
    const menuAnalytics = useMemo(() => {
        let items = menuItems || [];
        if (menuCatFilter !== 'all') {
            items = items.filter(i => i.category === menuCatFilter);
        }
        const active = items.filter(i => i.is_active !== false);
        const inactive = items.filter(i => i.is_active === false);

        // By category (pie)
        const catMap = {};
        items.forEach(i => {
            const cat = i.category || 'other';
            const info = getCategoryInfo(cat);
            if (!catMap[cat]) catMap[cat] = { name: info.label, value: 0 };
            catMap[cat].value++;
        });
        const byCategory = Object.values(catMap);

        // All portions with costs
        const allPortions = [];
        items.forEach(item => {
            (item.portions || []).forEach(p => {
                const costPrice = Number(p.cost_price) || calcPortionCost(p);
                const sellingPrice = Number(p.selling_price ?? p.price) || 0;
                const margin = sellingPrice - costPrice;
                const marginPct = sellingPrice > 0 ? (margin / sellingPrice) * 100 : 0;
                allPortions.push({
                    itemName: item.name,
                    itemCategory: item.category,
                    portionName: p.name,
                    costPrice,
                    sellingPrice,
                    margin,
                    marginPct,
                    ingredientCount: (p.recipe?.length || 0) + (p.sub_items?.length || 0),
                });
            });
        });

        // Top 10 highest margin portions (bar)
        const topMargin = [...allPortions]
            .filter(p => p.sellingPrice > 0 && p.ingredientCount > 0)
            .sort((a, b) => b.marginPct - a.marginPct)
            .slice(0, 10)
            .map(p => ({
                name: `${p.itemName} (${p.portionName})`,
                margin: Number(p.marginPct.toFixed(1)),
                cost: Number(p.costPrice.toFixed(2)),
                price: Number(p.sellingPrice.toFixed(2)),
            }));

        // Lowest margin (risk) portions (bar)
        const lowMargin = [...allPortions]
            .filter(p => p.sellingPrice > 0 && p.ingredientCount > 0)
            .sort((a, b) => a.marginPct - b.marginPct)
            .slice(0, 10)
            .map(p => ({
                name: `${p.itemName} (${p.portionName})`,
                margin: Number(p.marginPct.toFixed(1)),
                cost: Number(p.costPrice.toFixed(2)),
                price: Number(p.sellingPrice.toFixed(2)),
            }));

        // Price distribution (area chart)
        const priceRanges = [
            { range: '£0-5', min: 0, max: 5 },
            { range: '£5-10', min: 5, max: 10 },
            { range: '£10-20', min: 10, max: 20 },
            { range: '£20-30', min: 20, max: 30 },
            { range: '£30-50', min: 30, max: 50 },
            { range: '£50+', min: 50, max: Infinity },
        ];
        const priceDist = priceRanges.map(r => ({
            name: r.range,
            count: allPortions.filter(p => p.sellingPrice >= r.min && p.sellingPrice < r.max).length,
        }));

        // Cost vs Selling scatter data (top 15)
        const costVsPrice = [...allPortions]
            .filter(p => p.sellingPrice > 0)
            .sort((a, b) => b.sellingPrice - a.sellingPrice)
            .slice(0, 15)
            .map(p => ({
                name: `${p.itemName.slice(0, 20)}`,
                cost: Number(p.costPrice.toFixed(2)),
                price: Number(p.sellingPrice.toFixed(2)),
                margin: Number(p.margin.toFixed(2)),
            }));

        // Allergen distribution
        const allergenMap = {};
        items.forEach(i => {
            (i.allergens || []).forEach(a => {
                allergenMap[a] = (allergenMap[a] || 0) + 1;
            });
        });
        const allergenDist = Object.entries(allergenMap).map(([name, count]) => ({
            allergen: name.toUpperCase(),
            count,
            full: items.length,
        }));

        // Items missing recipes
        const missingRecipe = items.filter(i =>
            !i.portions?.length || i.portions.some(p => (!p.recipe?.length && !p.sub_items?.length))
        ).length;

        // Avg margin
        const avgMargin = allPortions.length
            ? allPortions.reduce((s, p) => s + p.marginPct, 0) / allPortions.length
            : 0;

        // Avg cost
        const avgCost = allPortions.length
            ? allPortions.reduce((s, p) => s + p.costPrice, 0) / allPortions.length
            : 0;

        return {
            total: items.length,
            active: active.length,
            inactive: inactive.length,
            portionCount: allPortions.length,
            missingRecipe,
            avgMargin,
            avgCost,
            byCategory,
            topMargin,
            lowMargin,
            priceDist,
            costVsPrice,
            allergenDist,
        };
    }, [menuItems, menuCatFilter]);

    // ─── INVOICE ANALYTICS ───
    const invoiceAnalytics = useMemo(() => {
        const all = filterByDate(invoices || []);
        const totalAmount = all.reduce((s, i) => s + (i.grand_total || 0), 0);

        // Spend over time
        const { from, to } = getDateRangeFilter();
        const diffDays = Math.ceil((to - from) / (1000 * 60 * 60 * 24));
        const isDayView = diffDays <= 45;

        const byPeriod = {};
        all.forEach(inv => {
            const d = parseDate(inv.invoice_date || inv.created_at);
            const key = isDayView
                ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
                : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            const label = isDayView
                ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                : d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });

            if (!byPeriod[key]) byPeriod[key] = { period: label, spend: 0, count: 0 };
            byPeriod[key].spend += (inv.grand_total || 0);
            byPeriod[key].count++;
        });
        const spendTrend = Object.keys(byPeriod).sort().map(k => byPeriod[k]);

        return {
            count: all.length,
            totalAmount,
            avgAmount: all.length ? totalAmount / all.length : 0,
            spendTrend,
        };
    }, [invoices, filterByDate, getDateRangeFilter]);

    // ─── EPOS SALES ANALYTICS (LIVE DATA from epos_events) ───
    const eposData = useMemo(() => {
        const processed = filteredEposEvents;
        if (processed.length === 0) return null; // No data yet

        const totalOrders = processed.length;

        // Build a price lookup from our menu items: { menuItemName → sellingPrice }
        const priceLookup = {};
        menuItems.forEach(mi => {
            (mi.portions || []).forEach(p => {
                const price = Number(p.selling_price ?? p.price) || 0;
                // Key by menu item name + portion name for specific lookups
                priceLookup[`${mi.name}__${p.name}`] = price;
                // Fallback: just menu item name (use first portion found)
                if (!priceLookup[mi.name]) priceLookup[mi.name] = price;
            });
            // If no portions, use cost_price or 0
            if (!priceLookup[mi.name]) {
                priceLookup[mi.name] = Number(mi.selling_price ?? mi.price) || 0;
            }
        });

        let totalItemsSold = 0;
        let totalRevenue = 0;
        const itemMap = {}; // mappedName → { qty, revenue, portions: { portionName: qty } }
        const dailyMap = {}; // YYYY-MM-DD → { revenue, orders }
        const hourlyMap = {}; // hourLabel → revenue total

        processed.forEach(ev => {
            const evDate = ev.order_date ? parseDate(ev.order_date) : (ev.received_at || new Date());
            // Use London timezone for date bucketing (consistent with filter boundaries)
            const londonParts = evDate instanceof Date
                ? new Intl.DateTimeFormat('en-GB', {
                    timeZone: 'Europe/London',
                    year: 'numeric', month: '2-digit', day: '2-digit',
                    hour: '2-digit', minute: '2-digit', hour12: false,
                }).formatToParts(evDate)
                : null;
            const lGet = (type) => londonParts?.find(p => p.type === type)?.value || '';
            const dateKey = londonParts ? `${lGet('year')}-${lGet('month')}-${lGet('day')}` : '';
            const dayLabel = evDate instanceof Date
                ? evDate.toLocaleDateString('en-GB', { timeZone: 'Europe/London', day: '2-digit', month: 'short' })
                : '';
            const hour = londonParts ? parseInt(lGet('hour'), 10) : 12;
            const hourLabel = hour === 0 ? '12am' : hour < 12 ? `${hour}am` : hour === 12 ? '12pm' : `${hour - 12}pm`;

            let orderRevenue = 0;

            // Use processing_result.results for mapped items (authoritative source)
            const results = ev.processing_result?.results || [];
            const hasResults = results.length > 0;

            if (hasResults) {
                results.forEach(r => {
                    const qty = r.quantity_sold || 1;
                    if (r.status === 'processed') {
                        totalItemsSold += qty;
                        const mappedName = r.menu_item || 'Unknown';
                        const portionName = r.portion || 'Standard';
                        // Look up our selling price
                        const unitPrice = priceLookup[`${mappedName}__${portionName}`]
                            || priceLookup[mappedName]
                            || 0;
                        const itemRevenue = unitPrice * qty;

                        if (!itemMap[mappedName]) itemMap[mappedName] = { name: mappedName, revenue: 0, qty: 0, portions: {} };
                        itemMap[mappedName].qty += qty;
                        itemMap[mappedName].revenue += itemRevenue;
                        itemMap[mappedName].portions[portionName] = (itemMap[mappedName].portions[portionName] || 0) + qty;
                        orderRevenue += itemRevenue;
                    }
                    // Skip unmapped items — don't show EPOS names
                });
            } else {
                // Fallback for events without processing_result (shouldn't happen often)
                (ev.line_items || []).forEach(li => {
                    const qty = li.quantity || 1;
                    totalItemsSold += qty;
                    const name = li.epos_item_name || 'Unknown';
                    if (!itemMap[name]) itemMap[name] = { name, revenue: 0, qty: 0, portions: {} };
                    itemMap[name].qty += qty;
                    itemMap[name].portions['Standard'] = (itemMap[name].portions['Standard'] || 0) + qty;
                });
            }

            totalRevenue += orderRevenue;

            // Daily
            if (dateKey) {
                if (!dailyMap[dateKey]) dailyMap[dateKey] = { day: dayLabel, revenue: 0, orders: 0 };
                dailyMap[dateKey].revenue += orderRevenue;
                dailyMap[dateKey].orders += 1;
            }

            // Hourly
            hourlyMap[hourLabel] = (hourlyMap[hourLabel] || 0) + orderRevenue;
        });

        // Top sellers (by qty) with portion breakdown labels
        const topSellers = Object.values(itemMap)
            .sort((a, b) => b.qty - a.qty)
            .slice(0, 10)
            .map(item => ({
                ...item,
                portionLabel: Object.entries(item.portions || {})
                    .sort(([, a], [, b]) => b - a)
                    .map(([p, q]) => `${p}: ${q}`)
                    .join(', '),
            }));

        // Daily revenue sorted by date
        const dailyRevenue = Object.entries(dailyMap)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([, v]) => v);

        // Hourly sales sorted by hour
        const hourOrder = ['12am', '1am', '2am', '3am', '4am', '5am', '6am', '7am', '8am', '9am', '10am', '11am',
            '12pm', '1pm', '2pm', '3pm', '4pm', '5pm', '6pm', '7pm', '8pm', '9pm', '10pm', '11pm'];
        const hourlySales = hourOrder
            .filter(h => hourlyMap[h])
            .map(h => ({ hour: h, sales: Math.round(hourlyMap[h]) }));

        // By item for pie chart
        const byCategory = topSellers.length > 0
            ? topSellers.slice(0, 8).map(i => ({ name: i.name, value: i.revenue || i.qty }))
            : [];

        return {
            dailyRevenue,
            topSellers,
            byCategory,
            hourlySales,
            totalRevenue,
            totalOrders,
            avgTicket: totalOrders ? totalRevenue / totalOrders : 0,
            itemsSold: totalItemsSold,
        };
    }, [filteredEposEvents, menuItems]);
    /* ═══════════════════════════════════════ HELPERS ═══ */
    const formatTime = (date) => {
        if (!date) return '';
        const d = parseDate(date);
        const now = new Date();
        const diffMs = now - d;
        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        const diffHrs = Math.floor(diffMins / 60);
        if (diffHrs < 24) return `${diffHrs}h ago`;
        const diffDays = Math.floor(diffHrs / 24);
        if (diffDays === 1) return 'Yesterday';
        return `${diffDays}d ago`;
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'delivered': return '#22c55e';
            case 'pending': return '#3b82f6';
            case 'confirmed': case 'preparing': return '#c9a96e';
            case 'ready': return '#06b6d4';
            case 'cancelled': return '#ef4444';
            default: return '#6b7280';
        }
    };

    const adminViewRestaurantName = location.state?.restaurantName;
    const restaurantName = adminViewRestaurantName || userProfile?.restaurant_name || userProfile?.name || 'Restaurant';

    /* ═══════════════════════════════════════ RENDER ═══ */
    return (
        <div className="dashboard-page">
            {/* ─── Welcome Banner ─── */}
            <div className="dashboard-welcome">
                <div>
                    <h2 className="welcome-title">{restaurantName} Analytics 📊</h2>
                    <p className="welcome-subtitle">Comprehensive analytics across inventory, orders, menu & financials.</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <div className="welcome-date">
                        {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>
                    <button className="btn-refresh" onClick={fetchAll} title="Refresh"><MdRefresh /></button>
                    {/* <button
                        className="btn btn-sm btn-secondary"
                        disabled={seeding}
                        onClick={async () => {
                            if (!restaurantId) return;
                            setSeeding(true);
                            try {
                                await seedRestaurantDashboard(restaurantId);
                                toast.success('Sample data seeded! Refreshing...');
                                await fetchAll();
                            } catch (e) {
                                console.error(e);
                                toast.error('Seeding failed: ' + e.message);
                            } finally {
                                setSeeding(false);
                            }
                        }}
                    >
                        {seeding ? 'Seeding...' : '🌱 Seed Data'}
                    </button> */}
                </div>
            </div>

            {/* ═══ KPI STAT CARDS ═══ */}
            <div className="stats-grid stats-grid-6">
                {[
                    { label: 'Inventory Items', value: loading ? '...' : invStats?.totalItems || 0, icon: MdInventory2, color: 'primary', sub: `Value: £${(inventoryAnalytics.totalValue || 0).toFixed(2)}` },
                    { label: 'Active Orders', value: loading ? '...' : orderAnalytics.active, icon: MdShoppingCart, color: 'info', sub: `Total placed: ${orderAnalytics.total}` },
                    { label: 'Low Stock', value: loading ? '...' : inventoryAnalytics.lowStock, icon: MdWarning, color: inventoryAnalytics.lowStock > 0 ? 'danger' : 'success', sub: `Out of stock: ${inventoryAnalytics.outOfStock}` },
                    { label: 'Menu Items', value: loading ? '...' : menuAnalytics.total, icon: MdMenuBook, color: 'primary', sub: `Active: ${menuAnalytics.active} | Inactive: ${menuAnalytics.inactive}` },
                    { label: 'CK Spend', value: loading ? '...' : `£${(invoiceAnalytics.totalAmount || 0).toFixed(2)}`, icon: MdReceipt, color: 'warning', sub: `${invoiceAnalytics.count} invoices` },
                    { label: 'Avg Margin', value: loading ? '...' : `${menuAnalytics.avgMargin.toFixed(1)}%`, icon: MdTrendingUp, color: menuAnalytics.avgMargin >= 50 ? 'success' : 'warning', sub: `Avg cost: £${menuAnalytics.avgCost.toFixed(2)}` },
                ].map((stat, idx) => (
                    <div key={idx} className="dash-stat-card">
                        <div className="dash-stat-top">
                            <div className={`dash-stat-icon ${stat.color}`}><stat.icon /></div>
                        </div>
                        <div className="dash-stat-value">{stat.value}</div>
                        <div className="dash-stat-label">{stat.label}</div>
                        <div className="dash-stat-sub">{stat.sub}</div>
                    </div>
                ))}
            </div>

            {/* ═══ DATE RANGE FILTER BAR ═══ */}
            <div className="dash-filter-bar">
                <div className="dash-filter-section">
                    <MdFilterList className="dash-filter-icon" />
                    <span className="dash-filter-label">Date Range</span>
                    <div className="dash-filter-presets">
                        {[
                            { key: 'today', label: 'Today' },
                            { key: 'yesterday', label: 'Yesterday' },
                            { key: '3days', label: 'Last 3 Days' },
                            { key: 'week', label: '1 Week' },
                            { key: 'month', label: '1 Month' },
                            { key: 'custom', label: 'Custom' },
                        ].map(f => (
                            <button key={f.key} className={`dash-filter-btn ${dateRange === f.key ? 'active' : ''}`}
                                onClick={() => setDateRange(f.key)}>
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>
                {dateRange === 'custom' && (
                    <div className="dash-filter-custom">
                        <label>From: <input type="date" className="dash-date-input" value={customFrom}
                            onChange={e => setCustomFrom(e.target.value)} /></label>
                        <label>To: <input type="date" className="dash-date-input" value={customTo}
                            onChange={e => setCustomTo(e.target.value)} /></label>
                    </div>
                )}
            </div>

            {/* ═══ QUICK ACTIONS ═══ */}
            <div className="quick-actions">
                <h3 className="section-title">Quick Actions</h3>
                <div className="actions-grid">
                    {[
                        { label: 'Order from CK', icon: MdShoppingCart, path: '/restaurant/order' },
                        { label: 'My Inventory', icon: MdInventory2, path: '/restaurant/inventory' },
                        { label: 'Menu Management', icon: MdMenuBook, path: '/restaurant/menu' },
                        { label: 'Log Waste', icon: MdDelete, path: '/waste' },
                    ].map(action => (
                        <button key={action.label} className="action-card" onClick={() => navigate(action.path)}>
                            <action.icon className="action-icon" />
                            <span className="action-label">{action.label}</span>
                            <MdArrowForward className="action-arrow" />
                        </button>
                    ))}
                </div>
            </div>

            {/* ═══════════════════════════════════════
                INVENTORY ANALYTICS SECTION
               ═══════════════════════════════════════ */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
                    <h3 className="section-title" style={{ margin: 0 }}>📦 Inventory Analytics</h3>
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: -8 }}>Stock levels, values, and health across all inventory items</p>
                    <div className="dash-filter-presets">
                        <button className={`dash-filter-btn ${invTypeFilter === 'all' ? 'active' : ''}`} onClick={() => setInvTypeFilter('all')}>All Types</button>
                        <button className={`dash-filter-btn ${invTypeFilter === 'grocery' ? 'active' : ''}`} onClick={() => setInvTypeFilter('grocery')}>Grocery</button>
                        <button className={`dash-filter-btn ${invTypeFilter === 'raw_meat' ? 'active' : ''}`} onClick={() => setInvTypeFilter('raw_meat')}>Raw Meat</button>
                        <button className={`dash-filter-btn ${invTypeFilter === 'cooked_meat' ? 'active' : ''}`} onClick={() => setInvTypeFilter('cooked_meat')}>Cooked Meat</button>
                    </div>
                </div>

                <div className="dash-chart-row">
                    {/* Stock Health Pie */}
                    <div className="card dash-chart-card">
                        <div className="card-header"><h3>Stock Health Distribution</h3></div>
                        <div className="dash-chart-body">
                            {loading ? <div className="chart-loading">Loading...</div> : inventoryAnalytics.healthDist.length === 0 ? (
                                <div className="chart-empty">No inventory data</div>
                            ) : (
                                <ResponsiveContainer width="100%" height={260}>
                                    <PieChart>
                                        <Pie data={inventoryAnalytics.healthDist} cx="50%" cy="50%" labelLine={false} label={renderPieLabel}
                                            outerRadius={100} dataKey="value" strokeWidth={0}>
                                            {inventoryAnalytics.healthDist.map((_, i) => (
                                                <Cell key={i} fill={['#22c55e', '#f59e0b', '#ef4444'][i] || COLORS[i]} />
                                            ))}
                                        </Pie>
                                        <Tooltip contentStyle={tooltipStyle} />
                                        <Legend wrapperStyle={{ fontSize: 12 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>

                    {/* Inventory by Type Pie */}
                    <div className="card dash-chart-card">
                        <div className="card-header"><h3>Inventory Value by Type</h3></div>
                        <div className="dash-chart-body">
                            {loading ? <div className="chart-loading">Loading...</div> : inventoryAnalytics.byType.length === 0 ? (
                                <div className="chart-empty">No data</div>
                            ) : (
                                <ResponsiveContainer width="100%" height={260}>
                                    <PieChart>
                                        <Pie data={inventoryAnalytics.byType} cx="50%" cy="50%" labelLine={false}
                                            label={renderPieLabel} outerRadius={100} dataKey="value" nameKey="name" strokeWidth={0}>
                                            {inventoryAnalytics.byType.map((_, i) => (
                                                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip contentStyle={tooltipStyle} formatter={(v) => `£${Number(v).toFixed(2)}`} />
                                        <Legend wrapperStyle={{ fontSize: 12 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>
                </div>

                <div className="dash-chart-row">
                    {/* Top Items by Stock Value Bar */}
                    <div className="card dash-chart-card">
                        <div className="card-header"><h3>Top 10 Items by Stock Value</h3></div>
                        <div className="dash-chart-body">
                            {loading ? <div className="chart-loading">Loading...</div> : (
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={inventoryAnalytics.topByValue} layout="vertical" margin={{ left: 20, right: 20 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" />
                                        <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} tickFormatter={v => `£${v}`} />
                                        <YAxis dataKey="name" type="category" width={130} tick={({ x, y, payload }) => (
                                            <text x={x} y={y} dy={4} textAnchor="end" fontSize={10} fill="#9ca3af">
                                                <title>{payload.value}</title>
                                                {payload.value.length > 18 ? payload.value.slice(0, 16) + '…' : payload.value}
                                            </text>
                                        )} />
                                        <Tooltip contentStyle={tooltipStyle} formatter={(v) => `£${Number(v).toFixed(2)}`} />
                                        <Bar dataKey="value" fill="#c9a96e" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>

                    {/* Low Stock Alert Table */}
                    <div className="card dash-chart-card">
                        <div className="card-header">
                            <h3>⚠️ Low Stock Alerts</h3>
                            <button className="btn btn-sm btn-secondary" onClick={() => navigate('/restaurant/inventory')}>
                                View All <MdArrowForward />
                            </button>
                        </div>
                        <div className="card-body" style={{ padding: 0, maxHeight: 300, overflowY: 'auto' }}>
                            {loading ? <div className="chart-loading">Loading...</div> :
                                inventoryAnalytics.lowStockItems.length === 0 ? (
                                    <div className="chart-empty" style={{ color: '#22c55e' }}>✅ All items in stock!</div>
                                ) : (
                                    <div className="activity-list">
                                        {inventoryAnalytics.lowStockItems.map((item, idx) => (
                                            <div key={idx} className="activity-item">
                                                <div className="activity-dot" style={{
                                                    background: (item.current_stock || 0) <= 0 ? '#ef4444' : '#f59e0b'
                                                }} />
                                                <div className="activity-content">
                                                    <p className="activity-text" style={{ fontWeight: 600 }}>
                                                        {item.item_name || item.name}
                                                    </p>
                                                    <span className="activity-time">
                                                        Stock: {(item.current_stock || 0).toFixed(2)} {item.unit || ''} | Threshold: {item.low_stock_threshold || 5}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                        </div>
                    </div>
                </div>

                {/* Inventory by Category Bar */}
                <div className="card dash-chart-card">
                    <div className="card-header"><h3>Inventory Value by Category</h3></div>
                    <div className="dash-chart-body">
                        {loading ? <div className="chart-loading">Loading...</div> : (
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={inventoryAnalytics.byCategory} margin={{ left: 10, right: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" />
                                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9ca3af' }} angle={-35} textAnchor="end" height={60} />
                                    <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} tickFormatter={v => `£${v}`} />
                                    <Tooltip contentStyle={tooltipStyle} formatter={(v, name) => [name === 'value' ? `£${Number(v).toFixed(2)}` : v, name === 'value' ? 'Value' : 'Items']} />
                                    <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Value" />
                                    <Bar dataKey="count" fill="#c9a96e" radius={[4, 4, 0, 0]} name="Items" />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>
            </div>

            {/* ═══════════════════════════════════════
                CK ORDERS ANALYTICS SECTION
               ═══════════════════════════════════════ */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
                <h3 className="section-title" style={{ margin: 0 }}>🛒 CK Order Analytics</h3>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: -8 }}>Orders placed to Central Kitchen — supply chain tracking</p>

                {/* KPI Row */}
                <div className="kpi-row">
                    <div className="kpi-card">
                        <div className="kpi-value">{loading ? '...' : orderAnalytics.total}</div>
                        <div className="kpi-label">Total Orders</div>
                    </div>
                    <div className="kpi-card">
                        <div className="kpi-value" style={{ color: '#22c55e' }}>{loading ? '...' : orderAnalytics.delivered}</div>
                        <div className="kpi-label">Delivered</div>
                    </div>
                    <div className="kpi-card">
                        <div className="kpi-value">£{loading ? '...' : orderAnalytics.totalAmount.toFixed(2)}</div>
                        <div className="kpi-label">Total Spend</div>
                    </div>
                    <div className="kpi-card">
                        <div className="kpi-value">£{loading ? '...' : orderAnalytics.avgValue.toFixed(2)}</div>
                        <div className="kpi-label">Avg Order Value</div>
                    </div>
                </div>

                <div className="dash-chart-row">
                    {/* Order Trend */}
                    <div className="card dash-chart-card">
                        <div className="card-header"><h3>Order Trend</h3></div>
                        <div className="dash-chart-body">
                            {loading ? <div className="chart-loading">Loading...</div> : orderAnalytics.trend.length === 0 ? (
                                <div className="chart-empty">No order data yet</div>
                            ) : (
                                <ResponsiveContainer width="100%" height={280}>
                                    <AreaChart data={orderAnalytics.trend} margin={{ left: 10, right: 20 }}>
                                        <defs>
                                            <linearGradient id="orderGrad" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#c9a96e" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#c9a96e" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" />
                                        <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                                        <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#9ca3af' }} tickFormatter={v => `£${v}`} />
                                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                                        <Tooltip contentStyle={tooltipStyle} />
                                        <Area yAxisId="left" type="monotone" dataKey="amount" stroke="#c9a96e" fill="url(#orderGrad)" strokeWidth={2} name="Amount (£)" />
                                        <Line yAxisId="right" type="monotone" dataKey="orders" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3, fill: '#3b82f6' }} name="Orders" />
                                        <Legend wrapperStyle={{ fontSize: 12 }} />
                                    </AreaChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>

                    {/* Orders by Status Pie */}
                    <div className="card dash-chart-card">
                        <div className="card-header"><h3>Orders by Status</h3></div>
                        <div className="dash-chart-body">
                            {loading ? <div className="chart-loading">Loading...</div> : orderAnalytics.byStatus.length === 0 ? (
                                <div className="chart-empty">No data</div>
                            ) : (
                                <ResponsiveContainer width="100%" height={280}>
                                    <PieChart>
                                        <Pie data={orderAnalytics.byStatus} cx="50%" cy="50%" labelLine={false}
                                            label={renderPieLabel} outerRadius={100} dataKey="value" strokeWidth={0}>
                                            {orderAnalytics.byStatus.map((entry, i) => (
                                                <Cell key={i} fill={getStatusColor(entry.name)} />
                                            ))}
                                        </Pie>
                                        <Tooltip contentStyle={tooltipStyle} />
                                        <Legend wrapperStyle={{ fontSize: 12 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>
                </div>

                {/* Recent Activity */}
                <div className="card">
                    <div className="card-header">
                        <h3>Recent Orders</h3>
                        <button className="btn btn-sm btn-secondary" onClick={() => navigate('/restaurant/orders')}>
                            View All <MdArrowForward />
                        </button>
                    </div>
                    <div className="card-body" style={{ padding: 0 }}>
                        <div className="activity-list">
                            {loading ? (
                                Array.from({ length: 3 }).map((_, i) => (
                                    <div key={i} className="activity-item">
                                        <div className="activity-dot" style={{ background: 'var(--color-text-muted)' }} />
                                        <div className="activity-content">
                                            <div className="skeleton skeleton-text" style={{ height: 14, width: '60%' }} />
                                        </div>
                                    </div>
                                ))
                            ) : orderAnalytics.recent.length === 0 ? (
                                <div className="activity-item">
                                    <div className="activity-dot" style={{ background: 'var(--color-text-muted)' }} />
                                    <div className="activity-content">
                                        <p className="activity-text">No recent orders. Place an order to get started!</p>
                                    </div>
                                </div>
                            ) : (
                                orderAnalytics.recent.map((order, idx) => {
                                    const num = order.order_number || order.id?.slice(0, 8);
                                    const items = order.items?.length || 0;
                                    const total = (order.total || order.total_amount || 0).toFixed(2);
                                    return (
                                        <div key={idx} className="activity-item">
                                            <div className="activity-dot" style={{ background: getStatusColor(order.status) }} />
                                            <div className="activity-content">
                                                <p className="activity-text">
                                                    Order #{num} — {items} items, £{total}
                                                    <span style={{
                                                        marginLeft: 8, fontSize: 11, fontWeight: 600,
                                                        padding: '1px 8px', borderRadius: 99,
                                                        background: `${getStatusColor(order.status)}20`,
                                                        color: getStatusColor(order.status),
                                                    }}>
                                                        {order.status}
                                                    </span>
                                                </p>
                                                <span className="activity-time">{formatTime(order.created_at)}</span>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ═══════════════════════════════════════
                MENU ANALYTICS SECTION
               ═══════════════════════════════════════ */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
                    <h3 className="section-title" style={{ margin: 0 }}>🍽️ Menu Analytics</h3>
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: -8 }}>Menu item costs, margins, pricing analysis, and allergen coverage</p>
                    <div className="dash-filter-presets">
                        <button className={`dash-filter-btn ${menuCatFilter === 'all' ? 'active' : ''}`} onClick={() => setMenuCatFilter('all')}>All</button>
                        {MENU_CATEGORIES.map(cat => (
                            <button key={cat.value} className={`dash-filter-btn ${menuCatFilter === cat.value ? 'active' : ''}`}
                                onClick={() => setMenuCatFilter(cat.value)}>
                                {cat.icon} {cat.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Menu KPIs */}
                <div className="kpi-row">
                    <div className="kpi-card">
                        <div className="kpi-value">{loading ? '...' : menuAnalytics.total}</div>
                        <div className="kpi-label">Menu Items</div>
                    </div>
                    <div className="kpi-card">
                        <div className="kpi-value">{loading ? '...' : menuAnalytics.portionCount}</div>
                        <div className="kpi-label">Total Portions</div>
                    </div>
                    <div className="kpi-card">
                        <div className="kpi-value" style={{ color: menuAnalytics.avgMargin >= 50 ? '#22c55e' : '#f59e0b' }}>
                            {loading ? '...' : `${menuAnalytics.avgMargin.toFixed(1)}%`}
                        </div>
                        <div className="kpi-label">Avg Profit Margin</div>
                    </div>
                    <div className="kpi-card">
                        <div className="kpi-value" style={{ color: menuAnalytics.missingRecipe > 0 ? '#ef4444' : '#22c55e' }}>
                            {loading ? '...' : menuAnalytics.missingRecipe}
                        </div>
                        <div className="kpi-label">Missing Recipes</div>
                    </div>
                </div>

                <div className="dash-chart-row">
                    {/* Menu Items by Category Pie */}
                    <div className="card dash-chart-card">
                        <div className="card-header"><h3>Items by Category</h3></div>
                        <div className="dash-chart-body">
                            {loading ? <div className="chart-loading">Loading...</div> : menuAnalytics.byCategory.length === 0 ? (
                                <div className="chart-empty">No menu items</div>
                            ) : (
                                <ResponsiveContainer width="100%" height={280}>
                                    <PieChart>
                                        <Pie data={menuAnalytics.byCategory} cx="50%" cy="50%" labelLine={false}
                                            label={renderPieLabel} outerRadius={100} dataKey="value" strokeWidth={0}>
                                            {menuAnalytics.byCategory.map((_, i) => (
                                                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip contentStyle={tooltipStyle} />
                                        <Legend wrapperStyle={{ fontSize: 12 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>

                    {/* Allergen Bar Chart */}
                    <div className="card dash-chart-card">
                        <div className="card-header"><h3>Allergen Coverage</h3></div>
                        <div className="dash-chart-body">
                            <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: '0 0 8px 8px' }}>Number of menu items containing each allergen</p>
                            {loading ? <div className="chart-loading">Loading...</div> : menuAnalytics.allergenDist.length === 0 ? (
                                <div className="chart-empty">No allergen data</div>
                            ) : (
                                <ResponsiveContainer width="100%" height={260}>
                                    <BarChart data={menuAnalytics.allergenDist} layout="vertical" margin={{ left: 10, right: 20 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" />
                                        <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} allowDecimals={false} />
                                        <YAxis dataKey="allergen" type="category" tick={{ fontSize: 11, fill: '#9ca3af' }} width={55} />
                                        <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${v} items`} />
                                        <Bar dataKey="count" fill="#f59e0b" radius={[0, 4, 4, 0]} name="Items" />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>
                </div>

                {/* Top Margin Portions */}
                <div className="card dash-chart-card">
                    <div className="card-header"><h3>🏆 Top 10 Profitable Portions (by Margin %)</h3></div>
                    <div className="dash-chart-body">
                        {loading ? <div className="chart-loading">Loading...</div> : menuAnalytics.topMargin.length === 0 ? (
                            <div className="chart-empty">Add recipes to see margin data</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={350}>
                                <BarChart data={menuAnalytics.topMargin} layout="vertical" margin={{ left: 20, right: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" />
                                    <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} domain={[0, 100]} tickFormatter={v => `${v}%`} />
                                    <YAxis dataKey="name" type="category" width={150} tick={({ x, y, payload }) => (
                                        <text x={x} y={y} dy={4} textAnchor="end" fontSize={10} fill="#9ca3af">
                                            <title>{payload.value}</title>
                                            {payload.value.length > 22 ? payload.value.slice(0, 20) + '…' : payload.value}
                                        </text>
                                    )} />
                                    <Tooltip contentStyle={tooltipStyle}
                                        formatter={(v) => `${v}%`}
                                        labelFormatter={name => name} />
                                    <Bar dataKey="margin" fill="#22c55e" radius={[0, 4, 4, 0]} name="Margin %" />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                {/* Low Margin Alert */}
                <div className="card dash-chart-card">
                    <div className="card-header"><h3>⚠️ Lowest Margin Portions (Review Pricing)</h3></div>
                    <div className="dash-chart-body">
                        {loading ? <div className="chart-loading">Loading...</div> : menuAnalytics.lowMargin.length === 0 ? (
                            <div className="chart-empty">Add recipes to see margin data</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={350}>
                                <BarChart data={menuAnalytics.lowMargin} layout="vertical" margin={{ left: 20, right: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" />
                                    <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} tickFormatter={v => `${v}%`} />
                                    <YAxis dataKey="name" type="category" width={150} tick={({ x, y, payload }) => (
                                        <text x={x} y={y} dy={4} textAnchor="end" fontSize={10} fill="#9ca3af">
                                            <title>{payload.value}</title>
                                            {payload.value.length > 22 ? payload.value.slice(0, 20) + '…' : payload.value}
                                        </text>
                                    )} />
                                    <Tooltip contentStyle={tooltipStyle}
                                        formatter={(v) => `${v}%`} />
                                    <Bar dataKey="margin" fill="#ef4444" radius={[0, 4, 4, 0]} name="Margin %" />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                <div className="dash-chart-row">
                    {/* Cost vs Selling Price */}
                    <div className="card dash-chart-card">
                        <div className="card-header"><h3>Cost vs Selling Price (Top 15)</h3></div>
                        <div className="dash-chart-body">
                            {loading ? <div className="chart-loading">Loading...</div> : menuAnalytics.costVsPrice.length === 0 ? (
                                <div className="chart-empty">No data</div>
                            ) : (
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={menuAnalytics.costVsPrice} margin={{ left: 10, right: 20 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" />
                                        <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#9ca3af' }} angle={-40} textAnchor="end" height={70} />
                                        <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} tickFormatter={v => `£${v}`} />
                                        <Tooltip contentStyle={tooltipStyle} formatter={v => `£${v}`} />
                                        <Legend wrapperStyle={{ fontSize: 12 }} />
                                        <Bar dataKey="cost" fill="#ef4444" radius={[4, 4, 0, 0]} name="Cost" />
                                        <Bar dataKey="price" fill="#22c55e" radius={[4, 4, 0, 0]} name="Selling Price" />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>

                    {/* Price Distribution */}
                    <div className="card dash-chart-card">
                        <div className="card-header"><h3>Price Range Distribution</h3></div>
                        <div className="dash-chart-body">
                            {loading ? <div className="chart-loading">Loading...</div> : (
                                <ResponsiveContainer width="100%" height={300}>
                                    <AreaChart data={menuAnalytics.priceDist} margin={{ left: 10, right: 20 }}>
                                        <defs>
                                            <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" />
                                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                                        <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} />
                                        <Tooltip contentStyle={tooltipStyle} />
                                        <Area type="monotone" dataKey="count" stroke="#8b5cf6" fill="url(#priceGrad)" strokeWidth={2} name="Portions" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ═══════════════════════════════════════
                FINANCIAL / CK SPEND ANALYTICS
               ═══════════════════════════════════════ */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
                <h3 className="section-title" style={{ margin: 0 }}>💰 CK Spend Analytics</h3>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: -8 }}>Central Kitchen procurement spend and invoice tracking</p>

                <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                    <div className="kpi-card">
                        <div className="kpi-value">£{loading ? '...' : invoiceAnalytics.totalAmount.toFixed(2)}</div>
                        <div className="kpi-label">Total CK Spend</div>
                    </div>
                    <div className="kpi-card">
                        <div className="kpi-value">{loading ? '...' : invoiceAnalytics.count}</div>
                        <div className="kpi-label">Invoices Received</div>
                    </div>
                    <div className="kpi-card">
                        <div className="kpi-value">£{loading ? '...' : invoiceAnalytics.avgAmount.toFixed(2)}</div>
                        <div className="kpi-label">Avg Invoice Value</div>
                    </div>
                </div>

                <div className="card dash-chart-card">
                    <div className="card-header"><h3>CK Spend Trend</h3></div>
                    <div className="dash-chart-body">
                        {loading ? <div className="chart-loading">Loading...</div> : invoiceAnalytics.spendTrend.length === 0 ? (
                            <div className="chart-empty">No invoice data yet</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={invoiceAnalytics.spendTrend} margin={{ left: 10, right: 20 }}>
                                    <defs>
                                        <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" />
                                    <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                                    <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#9ca3af' }} tickFormatter={v => `£${v}`} />
                                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                                    <Tooltip contentStyle={tooltipStyle} formatter={(value, name) => {
                                        if (name === 'Spend (£)') return [`£${Number(value).toFixed(2)}`, name];
                                        return [value, name];
                                    }} />
                                    <Area yAxisId="left" type="monotone" dataKey="spend" stroke="#f59e0b" fill="url(#spendGrad)" strokeWidth={2} name="Spend (£)" />
                                    <Line yAxisId="right" type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6', r: 3 }} name="Invoice Count" />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>
            </div>

            {/* ═══════════════════════════════════════
                EPOS SALES ANALYTICS (LIVE DATA)
               ═══════════════════════════════════════ */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
                <div>
                    <h3 className="section-title" style={{ marginBottom: '10px' }}>📈 Sales Analytics (EPOS)</h3>
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: -8 }}>
                        Revenue, top sellers, and sales trends from live EPOS data
                    </p>
                </div>

                {!eposData ? (
                    <div className="card" style={{
                        padding: 'var(--space-8)', textAlign: 'center',
                        color: 'var(--color-text-muted)',
                    }}>
                        <MdRestaurantMenu style={{ fontSize: 40, display: 'block', margin: '0 auto var(--space-3)', opacity: 0.4 }} />
                        <h4 style={{ color: 'var(--color-text-secondary)', marginBottom: 4 }}>No EPOS Sales Data Yet</h4>
                        <p style={{ fontSize: 'var(--text-sm)', maxWidth: 420, margin: '0 auto' }}>
                            Sales analytics will appear here once your EPOS system starts sending data via the webhook integration.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="kpi-row">
                            <div className="kpi-card">
                                <div className="kpi-value" style={{ color: '#22c55e' }}>£{eposData.totalRevenue.toLocaleString()}</div>
                                <div className="kpi-label">Total Revenue</div>
                            </div>
                            <div className="kpi-card">
                                <div className="kpi-value">{eposData.totalOrders}</div>
                                <div className="kpi-label">Total Sales Orders</div>
                            </div>
                            <div className="kpi-card">
                                <div className="kpi-value">£{eposData.avgTicket.toFixed(2)}</div>
                                <div className="kpi-label">Avg Ticket Size</div>
                            </div>
                            <div className="kpi-card">
                                <div className="kpi-value">{eposData.itemsSold}</div>
                                <div className="kpi-label">Items Sold</div>
                            </div>
                        </div>

                        {eposData.dailyRevenue.length > 0 && (
                            <div className="dash-chart-row">
                                <div className="card dash-chart-card">
                                    <div className="card-header"><h3>Daily Revenue Trend</h3></div>
                                    <div className="dash-chart-body">
                                        <ResponsiveContainer width="100%" height={280}>
                                            <AreaChart data={eposData.dailyRevenue} margin={{ left: 10, right: 20 }}>
                                                <defs>
                                                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                                                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" />
                                                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                                                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} tickFormatter={v => `£${v}`} />
                                                <Tooltip
                                                    contentStyle={tooltipStyle}
                                                    formatter={(value, name) => {
                                                        if (name === 'EPOS Revenue') return [`£${Number(value).toFixed(2)}`, name];
                                                        if (name === 'EPOS Orders') return [value, name]; // count — no £
                                                        return [value, name];
                                                    }}
                                                />
                                                <Area type="monotone" dataKey="revenue" stroke="#22c55e" fill="url(#revGrad)" strokeWidth={2} name="EPOS Revenue" />
                                                <Line type="monotone" dataKey="orders" stroke="#c9a96e" strokeWidth={2} dot={false} name="EPOS Orders" />
                                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>

                                {eposData.byCategory.length > 0 && (
                                    <div className="card dash-chart-card">
                                        <div className="card-header"><h3>Sales by Item</h3></div>
                                        <div className="dash-chart-body">
                                            <ResponsiveContainer width="100%" height={280}>
                                                <PieChart>
                                                    <Pie data={eposData.byCategory} cx="50%" cy="50%" labelLine={false}
                                                        label={renderPieLabel} outerRadius={100} dataKey="value" strokeWidth={0}>
                                                        {eposData.byCategory.map((_, i) => (
                                                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip contentStyle={tooltipStyle} formatter={v => `£${v}`} />
                                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {eposData.topSellers.length > 0 && (
                            <div className="card dash-chart-card">
                                <div className="card-header"><h3>Top Sellers (by Quantity)</h3></div>
                                <div className="dash-chart-body">
                                    <ResponsiveContainer width="100%" height={Math.max(200, eposData.topSellers.length * 36)}>
                                        <BarChart data={eposData.topSellers} layout="vertical" margin={{ left: 20, right: 20 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" />
                                            <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                                            <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: '#9ca3af' }} width={140} />
                                            <Tooltip
                                                contentStyle={tooltipStyle}
                                                content={({ active, payload }) => {
                                                    if (!active || !payload?.[0]) return null;
                                                    const d = payload[0].payload;
                                                    return (
                                                        <div style={{ ...tooltipStyle, padding: '8px 12px', fontSize: 12 }}>
                                                            <div style={{ fontWeight: 700, marginBottom: 4 }}>{d.name}</div>
                                                            <div>Total Sold: <strong>{d.qty}</strong> units</div>
                                                            <div>Revenue: <strong>£{(d.revenue || 0).toFixed(2)}</strong></div>
                                                            {d.portionLabel && (
                                                                <div style={{ marginTop: 4, fontSize: 11, color: '#9ca3af', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 4 }}>
                                                                    {d.portionLabel}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                }}
                                            />
                                            <Bar dataKey="qty" fill="#c9a96e" radius={[0, 4, 4, 0]} name="Quantity Sold" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        )}

                        {/* Item-wise Sales Trends Table */}
                        <EposSalesTable
                            eposEvents={filteredEposEvents}
                            menuItems={menuItems}
                            inventoryItems={inventoryItems}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default RestaurantDashboard;
